import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  FaGamepad, FaUsers, FaMobileAlt, FaBrain, FaGlobe,
  FaMagic, FaCogs, FaRocket, FaCheck,
  FaApple, FaAndroid, FaChevronRight,
  FaShieldAlt, FaTrophy, FaInstagram, FaHourglassHalf,
} from 'react-icons/fa';
import { FaXTwitter, FaTiktok } from 'react-icons/fa6';
import { motion, AnimatePresence } from 'framer-motion';
import { heroData } from '../utils/data';
import { useLang } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';

const featureIcons = [
  <FaGamepad />, <FaRocket />, <FaUsers />, <FaMobileAlt />,
  <FaBrain />, <FaGlobe />, <FaMagic />, <FaCogs />, <FaShieldAlt />,
];

const platformIcons = { iOS: FaApple, Android: FaAndroid };

const PlatformBadge = ({ name }) => {
  const Icon = platformIcons[name] || FaMobileAlt;
  return (
    <span className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 text-gray-400 text-xs font-medium px-2.5 py-1 rounded-full">
      <Icon size={9} />{name}
    </span>
  );
};

const chunkArray = (arr, size) =>
  Array.from({ length: Math.ceil(arr.length / size) }, (_, i) => arr.slice(i * size, i * size + size));

const MainContainer = () => {
  const { lang } = useLang();
  const t = translations[lang].home;
  const tGD = translations[lang].gameData;

  const features = t.features.map((f, i) => ({ ...f, icon: featureIcons[i] }));

  const [page, setPage] = useState(0);
  const groups = chunkArray(features, 3);
  const intervalRef = useRef(null);
  const featured = heroData.find(g => g.isFeatured) || heroData[0];
  const featuredTranslated = tGD[featured.id] || {};

  const startSlide = () => { intervalRef.current = setInterval(() => setPage(p => (p + 1) % groups.length), 5000); };
  const stopSlide = () => clearInterval(intervalRef.current);

  useEffect(() => { startSlide(); return () => clearInterval(intervalRef.current); }, [groups.length]);

  return (
    <div className="w-full">

      {/* ══════════ HERO + STATS ══════════ */}
      <section className="relative w-full h-screen flex flex-col overflow-hidden bg-gray-950">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)', backgroundSize: '50px 50px' }} />
        <div className="absolute top-1/3 right-1/3 w-[600px] h-[600px] bg-headingColor/10 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[300px] bg-headingColor/8 rounded-full blur-[100px] pointer-events-none" />

        <div className="relative z-10 flex-1 max-w-7xl mx-auto px-6 w-full grid grid-cols-1 lg:grid-cols-2 gap-16 items-center pt-14">
          <div className="flex flex-col gap-7">
            <motion.span
              className="inline-flex items-center gap-2 bg-headingColor/10 border border-headingColor/25 text-red-400 text-xs font-bold tracking-widest uppercase px-3.5 py-1.5 rounded-full w-fit"
              initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}
            >
              {t.badge}
            </motion.span>

            <motion.h1
              className="text-6xl md:text-7xl xl:text-8xl font-black text-white leading-[0.92] tracking-tighter"
              initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            >
              {t.title[0]}<br />{t.title[1]}<br /><span className="text-headingColor">{t.title[2]}</span>
            </motion.h1>

            <motion.p
              className="text-gray-400 text-base md:text-lg leading-relaxed max-w-[440px]"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.3 }}
            >
              {t.subtitle}
            </motion.p>

            <motion.div className="flex flex-wrap gap-3" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.45 }}>
              <Link to="/games" className="inline-flex items-center gap-2 bg-headingColor hover:bg-red-700 text-white px-7 py-3.5 rounded-xl font-bold text-sm shadow-2xl shadow-red-950/60 transition-all duration-200 hover:-translate-y-0.5">
                <FaGamepad className="text-sm" /> {t.btn1}
              </Link>
              <Link to={`/${featured.slug}`} className="inline-flex items-center gap-2 border border-white/10 hover:border-white/30 text-gray-300 hover:text-white px-7 py-3.5 rounded-xl font-bold text-sm transition-all duration-200 hover:-translate-y-0.5">
                {t.btn2}
              </Link>
            </motion.div>

            <motion.div className="flex items-center gap-3 pt-1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.6 }}>
              <span className="text-gray-600 text-[10px] font-bold tracking-widest uppercase">{t.platformsLabel}</span>
              <div className="flex gap-2">{['iOS', 'Android'].map(p => <PlatformBadge key={p} name={p} />)}</div>
            </motion.div>
          </div>

          <motion.div
            className="hidden lg:flex items-center justify-center relative"
            initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="absolute inset-0 bg-headingColor/20 rounded-full blur-[80px] pointer-events-none" />
            <motion.img
              src={featured.imageSrc} alt={featured.name}
              className="relative z-10 w-[320px] xl:w-[400px] rounded-3xl shadow-2xl shadow-black/70"
              animate={{ y: [0, -16, 0] }} transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
            />
            <div className="absolute -top-3 -right-3 bg-headingColor text-white text-xs font-black px-3 py-1.5 rounded-full shadow-lg z-20 tracking-wide">{t.featuredBadge}</div>
          </motion.div>
        </div>

        {/* Stats */}
        <div className="relative z-10 w-full bg-headingColor">
          <div className="max-w-5xl mx-auto px-6 py-7 grid grid-cols-2 md:grid-cols-4 gap-5">
            {t.stats.map(([v, l], i) => (
              <motion.div key={i} className="text-center" initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                <p className="text-4xl font-black text-white">{v}</p>
                <p className="text-red-200 text-[10px] font-bold tracking-widest uppercase mt-1">{l}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ FEATURED GAME ══════════ */}
      <section className="w-full bg-white py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div className="text-center mb-14" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="text-headingColor text-xs font-bold tracking-widest uppercase mb-3">{t.featuredEyebrow}</p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight">{t.featuredTitle}</h2>
          </motion.div>

          <motion.div
            className="bg-gray-950 rounded-3xl overflow-hidden grid grid-cols-1 lg:grid-cols-5"
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          >
            <div className="lg:col-span-2 relative flex items-center justify-center p-12 min-h-[320px]">
              <div className="absolute inset-0 bg-gradient-to-br from-headingColor/20 to-transparent" />
              <img src={featured.imageSrc} alt={featured.name} className="relative z-10 w-full max-w-[220px] rounded-2xl shadow-2xl shadow-black/70" />
            </div>

            <div className="lg:col-span-3 p-8 md:p-12 flex flex-col justify-center gap-5 border-l border-white/5">
              <div className="flex flex-wrap gap-2">
                {(featuredTranslated.tags || featured.tags)?.map(tag => (
                  <span key={tag} className="bg-white/5 border border-white/8 text-gray-500 text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full">{tag}</span>
                ))}
              </div>
              <h3 className="text-5xl md:text-6xl font-black text-white tracking-tighter">{featured.name}</h3>
              <p className="text-headingColor text-sm font-semibold italic">{featuredTranslated.tagline || featured.tagline}</p>
              <p className="text-gray-400 text-sm leading-relaxed">{featuredTranslated.shortDescription || featured.shortDescription}</p>

              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 bg-yellow-500/10 border border-yellow-500/25 text-yellow-400 text-[10px] font-bold px-3 py-1 rounded-full tracking-widest uppercase">
                  <FaHourglassHalf size={9} /> {lang === 'tr' ? "Yakında App Store & Google Play'de" : 'Coming Soon to App Store & Google Play'}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(featuredTranslated.features || featured.features)?.slice(0, 4).map(f => (
                  <div key={f} className="flex items-center gap-2 text-gray-300 text-sm">
                    <FaCheck className="text-headingColor text-xs shrink-0" />{f}
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2">
                {featured.platforms?.map(p => <PlatformBadge key={p} name={p} />)}
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <Link to={`/${featured.slug}`} className="inline-flex items-center gap-2 bg-headingColor hover:bg-red-700 text-white px-7 py-3 rounded-xl font-bold text-sm transition-all duration-200 hover:-translate-y-0.5 shadow-xl shadow-red-950/40">
                  {t.details} <FaChevronRight size={9} />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════ WHY HARMAN PLAY ══════════ */}
      <section className="w-full bg-gray-50 py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="text-headingColor text-xs font-bold tracking-widest uppercase mb-3">{t.whyEyebrow}</p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight">{t.whyTitle}</h2>
          </motion.div>

          <div onMouseEnter={stopSlide} onMouseLeave={startSlide}>
            <AnimatePresence mode="wait">
              <motion.div key={page} className="grid grid-cols-1 md:grid-cols-3 gap-5" initial={{ opacity: 0, y: -18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 18 }} transition={{ duration: 0.5 }}>
                {groups[page].map((f, i) => (
                  <motion.div key={i} whileHover={{ y: -5 }} transition={{ type: 'spring', stiffness: 300 }}
                    className="group rounded-2xl p-7 bg-white border border-gray-100 hover:border-headingColor/20 hover:shadow-xl hover:shadow-headingColor/5 cursor-default transition-all duration-300">
                    <div className="w-11 h-11 rounded-xl bg-headingColor/8 group-hover:bg-headingColor flex items-center justify-center text-headingColor group-hover:text-white text-lg transition-all duration-300 mb-5">
                      {f.icon}
                    </div>
                    <h3 className="text-base font-black text-gray-900 mb-2">{f.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex justify-center gap-2 mt-10">
            {groups.map((_, i) => (
              <button key={i} onClick={() => { stopSlide(); setPage(i); startSlide(); }}
                className={`rounded-full transition-all duration-300 ${i === page ? 'bg-headingColor w-7 h-2' : 'bg-gray-200 w-2 h-2 hover:bg-gray-300'}`} />
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ CTA ══════════ */}
      <section className="w-full bg-gray-950 py-28 px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)', backgroundSize: '50px 50px' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[300px] bg-headingColor/10 rounded-full blur-[100px] pointer-events-none" />
        <motion.div className="relative z-10 max-w-2xl mx-auto text-center" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <FaTrophy className="text-headingColor/30 text-5xl mx-auto mb-6" />
          <h2 className="text-5xl md:text-7xl font-black text-white mb-5 tracking-tighter leading-none">
            {t.ctaTitle[0]}<br />{t.ctaTitle[1]}
          </h2>
          <p className="text-gray-500 text-base mb-10 leading-relaxed">
            {t.ctaSubtitle}
          </p>
          <Link to="/games" className="inline-flex items-center gap-2.5 bg-headingColor hover:bg-red-700 text-white px-10 py-4 rounded-xl font-bold text-sm shadow-2xl shadow-red-950/50 transition-all duration-200 hover:-translate-y-0.5">
            <FaGamepad className="text-sm" /> {t.ctaBtn}
          </Link>

          <div className="flex items-center justify-center gap-3 mt-10">
            {[
              { href: 'https://www.instagram.com/harmanplay/', Icon: FaInstagram, label: 'Instagram' },
              { href: 'https://x.com/harmanplay', Icon: FaXTwitter, label: 'X' },
              { href: 'https://www.tiktok.com/@harmanplay', Icon: FaTiktok, label: 'TikTok' },
            ].map(({ href, Icon, label }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
                className="w-11 h-11 bg-white/5 hover:bg-headingColor border border-white/10 hover:border-headingColor rounded-xl flex items-center justify-center text-gray-400 hover:text-white transition-all duration-200">
                <Icon size={16} />
              </a>
            ))}
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default MainContainer;
